import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ColumnSpan } from 'src/app/shared/form/columnSpan.model';
import { FormComponent, IFormDialogData } from 'src/app/shared/form/form.component';
import { ArrayFormField, ButtonFormField, FormField, TextFormField } from 'src/app/shared/form/formField.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { SensorAssignment, SensorAssignmentModelList } from 'src/app/models/sensorAssignment.model';
import { NodeAssignmentDialogComponent } from './node-assignment-dialog.component';
import { SensorAssignmentService } from 'src/app/services/sensorAssignment.service';
import { forkJoin } from 'rxjs';

export interface IMultipleNodeAssignmentFormDialogData extends IFormDialogData {
    sensorAssignmentIds: string[];
}

@Component({
  standalone: false,
    selector: 'app-multiple-node-assignment-dialog',
    templateUrl: './../../shared/form/form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./../../shared/form/form.component.scss'],
})
export class MultipleSensorAssignmentDialog extends FormComponent<SensorAssignmentModelList> implements OnInit {

    constructor(dialogRef: MatDialogRef<MultipleSensorAssignmentDialog>,
        private dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public data: IMultipleNodeAssignmentFormDialogData,
        protected injector: Injector,
        private sensorAssignmentService: SensorAssignmentService,
    ) {
        super(dialogRef, null, data, "Node Assignments", injector);
        this.isReadonly = true;
    }

    ngOnInit() {
        this.isFormLoading = true;
        this.initModel();
    }

    getNewObject(): SensorAssignmentModelList {
        return new SensorAssignmentModelList();
    }

    initModel(): void {
        this.entity = this.getNewObject();
        forkJoin(
            this.data.sensorAssignmentIds.map(sensorAssignmentId =>
                this.sensorAssignmentService.getById(sensorAssignmentId)
            )
        ).subscribe(sensorAssignments => {
            this.entity.sensorAssignments = sensorAssignments.map(sensorAssignment => ({
                ...sensorAssignment,
                startTime: this.dateFormatService.formatTimestampToString(
                    sensorAssignment.startDate as number,
                    this.dateFormatService.dateToTimeFormat
                ),
                endTime: sensorAssignment.endDate
                    ? this.dateFormatService.formatTimestampToString(
                        sensorAssignment.endDate as number,
                        this.dateFormatService.dateToTimeFormat
                    )
                    : undefined
            }));
            this.initFields();
            this.isFormLoading = false;
        });
    }

    validate(): boolean {
        return true;
    }

    initNodeAssignmentFields(): FormField[][] {
        return Array.from({ length: this.entity.sensorAssignments.length }, () => this.createNodeAssignmentFields());
    }

    openNodeAssignmentDialog(sensorAssignment: SensorAssignment) {

        const dialogRef = this.dialog.open(NodeAssignmentDialogComponent, {
            data: {
                id: sensorAssignment.id,
                nodeId: sensorAssignment.endPointId,
                nodeName: sensorAssignment.endPointName,
                sensorId: sensorAssignment.sensorId,
                sensorName: sensorAssignment.sensorName,
                organizationLevelId: sensorAssignment.organizationLevelId,
                isNodeAssignment: true
            },
            width: '550px',
            disableClose: true
        });
        
        this.handleDialogClose(dialogRef);
    }

    openNodeAssignmentDeleteDialog(sensorAssignmentId: string) {
        const dialogRef = this.dialog.open(DeleteDialog, {
            data: {
                id: sensorAssignmentId,
                formDeletes: true,
                service: this.sensorAssignmentService,
                title: "Node Assignment"
            },
            width: '440px',
            disableClose: true
        });

        this.handleDialogClose(dialogRef);
    }

    handleDialogClose(dialogRef: MatDialogRef<any, any>): void {
        dialogRef.afterClosed()
            .subscribe((result: any) => {
                if (result) {
                    this.close();
                }
            });
    }


    createNodeAssignmentFields(): FormField[] {
        const nodeAssignmentFields = [
            new TextFormField({
                property: nameOf<SensorAssignment>(x => x.sensorId),
                name: "Sensor",
                columnSpan: new ColumnSpan(2, 6),
                cssClass: "multiple-row",
                required: false,
                readonly: true
            }),
            new TextFormField({
                property: nameOf<SensorAssignment>(x => x.startTime),
                name: "Start Time",
                columnSpan: new ColumnSpan(1, 6),
                cssClass: "multiple-row",
                required: false,
                readonly: true
            }),
            new TextFormField({
                property: nameOf<SensorAssignment>(x => x.endTime),
                name: "End Time",
                columnSpan: new ColumnSpan(1, 6),
                cssClass: "multiple-row",
                required: false,
                readonly: true
            }),
            new ButtonFormField({
                property: nameOf<SensorAssignment>(x => x.id),
                name: "",
                columnSpan: new ColumnSpan(1, 6),
                cssClass: "multiple-row",
                iconClass: "fa fa-pen-square big-icon",
                required: false,
                onClick: (sensorAssignment: SensorAssignment) => {
                    this.openNodeAssignmentDialog(sensorAssignment);
                }
            }),
            new ButtonFormField({
                property: nameOf<SensorAssignment>(x => x.id),
                name: "",
                columnSpan: new ColumnSpan(1, 6),
                cssClass: "multiple-row",
                iconClass: "fa fa-trash big-icon",
                required: false,
                onClick: (sensorAssignment: SensorAssignment) => {
                    this.openNodeAssignmentDeleteDialog(sensorAssignment.id);
                }
            }),
        ];
        return nodeAssignmentFields;
    }

    onBeforeSave(model: SensorAssignmentModelList): void { }

    initFields() {
        this.fields = [
            new ArrayFormField({
                property: nameOf<SensorAssignmentModelList>(x => x.sensorAssignments), name: "",
                items: this.initNodeAssignmentFields(), cssClass: "multiple-rows", columnSpan: new ColumnSpan(1, 1)
            })
        ];
    }
}
